// A list (section 6.4): ordered rows with drag handles, the element's widget per row, remove per row, "+ Add" (a default
// element, or the multi-select picker for lists of documents); a reorder and a removal are each one edit of the whole list.
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMemo, useState, type ReactNode } from 'react';
import type { Json } from '../../api/types';
import { Button, IconButton } from '../../ui/Button';
import { GripVertical, Plus, X } from '../../ui/icons';
import { useContent } from '../ContentContext';
import { typeDefault } from '../schema';
import { FieldWidget } from './FieldWidget';
import { ReferencePicker } from './ReferenceField';
import { createDocumentDraft, isComponentTarget, targetsOf, useWidgetEnv, type WidgetProps } from './context';
import { setRel } from './InlineObjectField';

export function ListField({ param: p, value, onCommit, onCommitAt, disabled, id, owner }: WidgetProps) {
  const catalog = useContent();
  const { workspace } = useWidgetEnv();
  const items = useMemo(() => (Array.isArray(value) ? (value as Json[]) : []), [value]);
  const of = p.of;
  const [picking, setPicking] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  // stable row ids across reorders: the index at first render of each value identity
  const keys = useMemo(() => items.map((_, i) => `${p.name}-${i}`), [items, p.name]);
  if (!of) return <code className="small muted">{JSON.stringify(items).slice(0, 120)}</code>;

  const commitItem = (i: number, v: Json) => {
    if (onCommitAt) onCommitAt(`[${i}]`, v);
    else onCommit(items.map((x, j) => (j === i ? v : x)));
  };
  const remove = (i: number) => onCommit(items.filter((_, j) => j !== i));
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const from = keys.indexOf(String(e.active.id));
    const to = keys.indexOf(String(e.over.id));
    if (from < 0 || to < 0) return;
    onCommit(arrayMove(items, from, to));
  };
  const documentList = of.type === 'document';
  const componentList = documentList && isComponentTarget(catalog, of);
  const add = () => {
    if (componentList) {
      const t = targetsOf(catalog, of)[0];
      if (!t) return;
      onCommit([...items, createDocumentDraft(workspace, t)]);
      return;
    }
    if (documentList) {
      setPicking(true);
      return;
    }
    onCommit([...items, typeDefault(of)]);
  };
  return (
    <div className="list-field" id={id} data-testid={`list-${p.name}`}>
      {items.length === 0 && <span className="muted small">No items yet.</span>}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={keys} strategy={verticalListSortingStrategy}>
          <ol className="list-rows">
            {items.map((item, i) => (
              <SortableRow key={keys[i]} id={keys[i]!} disabled={disabled} onRemove={() => remove(i)} label={`${p.displayName ?? p.name} ${i + 1}`}>
                <FieldWidget param={{ ...of, name: `${p.name}[${i}]`, displayName: `${p.displayName ?? p.name} ${i + 1}` }} value={item} disabled={disabled} owner={owner} onCommit={(v) => commitItem(i, v)} onCommitAt={(rel, v) => (onCommitAt ? onCommitAt(`[${i}]${rel.startsWith('[') ? '' : '.'}${rel}`, v) : commitItem(i, setRel(item, rel, v)))} />
              </SortableRow>
            ))}
          </ol>
        </SortableContext>
      </DndContext>
      {!disabled && (
        <Button size="s" onClick={add} icon={<Plus aria-hidden="true" />}>
          Add
        </Button>
      )}
      {picking && (
        <ReferencePicker
          param={of}
          selected={items.filter((x): x is string => typeof x === 'string')}
          multi
          onClose={() => setPicking(false)}
          onPick={(ids) => {
            setPicking(false);
            const kept = items.filter((x) => typeof x === 'string' && ids.includes(x));
            const added = ids.filter((x) => !items.includes(x));
            onCommit([...kept, ...added]);
          }}
        />
      )}
    </div>
  );
}

function SortableRow({ id, disabled, onRemove, label, children }: { id: string; disabled?: boolean; onRemove: () => void; label: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return (
    <li ref={setNodeRef} className={`list-row ${isDragging ? 'dragging' : ''}`} style={{ transform: CSS.Transform.toString(transform), transition }}>
      <button type="button" className="drag-handle" aria-label={`Move ${label}`} disabled={disabled} {...attributes} {...listeners}>
        <GripVertical aria-hidden="true" />
      </button>
      <span className="list-row-body">{children}</span>
      {!disabled && (
        <IconButton label={`Remove ${label}`} onClick={onRemove}>
          <X aria-hidden="true" />
        </IconButton>
      )}
    </li>
  );
}
